import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import API, { getSchemaName } from 'shared/api';
import {
  FiTrendingUp,
  FiPackage,
  FiShoppingCart,
  FiShoppingBag,
  FiDollarSign,
  FiAlertTriangle,
  FiBarChart2,
  FiUser,
  FiUsers,
  FiArchive,
  FiCreditCard,
  FiCheckCircle,
  FiXCircle,
  FiMessageCircle,
  FiMapPin,
  FiTruck,
  FiList,
} from 'react-icons/fi';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { COL } from 'shared/darkColors';
import DashboardERP from './DashboardERP';
import {
  money,
  abbr,
  intFmt,
  pctFmt,
  Delta,
  Sparkline,
  Card,
  Empty,
  SkeletonBlock,
  LineChart,
  Donut,
  BarsH,
} from 'shared/components/Dashboard/charts';

// ── Sub-componentes locales ──
function Heatmap({ data }) {
  if (!data || data.length === 0) return <Empty />;
  const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const grid = {};
  let max = 0;
  data.forEach((c) => {
    grid[`${c.dow}_${c.hora}`] = c.monto;
    if (c.monto > max) max = c.monto;
  });
  const horas = Array.from({ length: 24 }, (_, h) => h);
  const cellColor = (v) => {
    if (!v) return COL.bg;
    const t = Math.min(1, v / (max || 1));
    return `rgb(${Math.round(233 - t * 214)},${Math.round(247 - t * 125)},${Math.round(238 - t * 165)})`;
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {dias.map((dn, dow) => (
            <tr key={dow}>
              <td
                style={{ fontSize: '10px', color: COL.muted, paddingRight: '6px', fontWeight: 600 }}
              >
                {dn}
              </td>
              {horas.map((h) => {
                const v = grid[`${dow}_${h}`] || 0;
                return (
                  <td
                    key={h}
                    title={`${dn} ${h}:00 — ${money(v)}`}
                    style={{
                      width: '13px',
                      height: '14px',
                      background: cellColor(v),
                      border: '1px solid #fff',
                    }}
                  />
                );
              })}
            </tr>
          ))}
          <tr>
            <td />
            {horas.map((h) => (
              <td key={h} style={{ fontSize: '7px', color: COL.muted, textAlign: 'center' }}>
                {h % 3 === 0 ? h : ''}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function MapCleanup() {
  const map = useMap();
  const ref = useRef(map);
  ref.current = map;
  useEffect(
    () => () => {
      try {
        ref.current?.remove();
      } catch (e) {
        /* noop */
      }
    },
    []
  );
  return null;
}

function MapaEntregas({ puntos, height = 340 }) {
  const pts = (puntos || []).filter((p) => p.lat && p.lng);
  if (pts.length === 0) return <Empty>Sin entregas con ubicación registrada</Empty>;
  const lat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
  const lng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
  const maxT = Math.max(...pts.map((p) => p.total || 1), 1);
  return (
    <div
      style={{
        width: '100%',
        height,
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${COL.border}`,
      }}
    >
      <MapContainer
        center={[lat, lng]}
        zoom={12}
        style={{ width: '100%', height: '100%' }}
        scrollWheelZoom={false}
      >
        <MapCleanup />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {pts.map((p, i) => (
          <CircleMarker
            key={i}
            center={[p.lat, p.lng]}
            radius={6 + 8 * Math.sqrt((p.total || 1) / maxT)}
            pathOptions={{ color: COL.purple, fillColor: COL.purple, fillOpacity: 0.5, weight: 1 }}
          >
            <Tooltip>
              {p.total} entrega{p.total !== 1 ? 's' : ''}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

function DashboardAdmin({ onNavigate }) {
  const { isPlanExterno, tieneVisibilidad } = useAuth();
  const v = (c) => tieneVisibilidad(c);
  const [tab, setTab] = useState(() => {
    try {
      const t = localStorage.getItem('agorax_dashboard_tab');
      if (t) {
        localStorage.removeItem('agorax_dashboard_tab');
        return t;
      }
    } catch (e) {}
    return 'ventas';
  });
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [syncERP, setSyncERP] = useState(null); // null=detectando, true/false resuelto
  const [sedes, setSedes] = useState([]);
  const [sede, setSede] = useState('');
  const [preset, setPreset] = useState('30d');
  const [fechaDesde, setFechaDesde] = useState(() => rangoDePreset('30d')[0]);
  const [fechaHasta, setFechaHasta] = useState(() => rangoDePreset('30d')[1]);
  const [armadores, setArmadores] = useState([]);
  const [deptos, setDeptos] = useState([]);
  const [departamento, setDepartamento] = useState('');

  useEffect(() => {
    API.get('/usuarios/sedes')
      .then((r) => {
        if (r.data.status === 'success') setSedes(r.data.data || []);
      })
      .catch(() => {});
    API.get('/inventario/departamentos')
      .then((r) => {
        if (r.data.status === 'success') setDeptos(r.data.data || []);
      })
      .catch(() => {});
    // Detectar si la empresa sincroniza un ERP (ventas externas).
    API.get('/ventas/erp/resumen')
      .then((r) => {
        setSyncERP(r.data.status === 'success' && r.data.data?.disponible === true);
      })
      .catch(() => setSyncERP(false));
  }, []);

  useEffect(() => {
    if (syncERP === null || syncERP === true) return; // sync ERP: no cargar dashboard nativo
    cargar();
  }, [sede, fechaDesde, fechaHasta, departamento, syncERP]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargar = async () => {
    setCargando(true);
    try {
      let url = '/inventario/dashboard?';
      if (sede) url += `sede=${sede}&`;
      if (fechaDesde) url += `fecha_desde=${fechaDesde}&`;
      if (fechaHasta) url += `fecha_hasta=${fechaHasta}&`;
      if (departamento) url += `departamento=${encodeURIComponent(departamento)}&`;
      const r = await API.get(url);
      if (r.data.status === 'success') setData(r.data.data);
    } catch {
    } finally {
      setCargando(false);
    }
    try {
      const params = new URLSearchParams();
      params.append('schema_name', getSchemaName());
      if (sede) params.append('codigo_sede', sede);
      const rm = await API.get(`/pedidos/armador/metricas?${params.toString()}`);
      if (rm.data.status === 'success') setArmadores(rm.data.data.armadores || []);
    } catch {}
  };

  const aplicarPreset = (id) => {
    setPreset(id);
    if (id === 'custom') return;
    const [a, b] = rangoDePreset(id);
    setFechaDesde(a);
    setFechaHasta(b);
  };

  const exportarCSV = () => {
    if (!data) return;
    const d = data;
    const rows = [];
    rows.push(['Métrica', 'Valor']);
    const ingresosActual = (d.ventas_pos?.ingresos || 0) + (d.pedidos_online?.ingresos || 0);
    rows.push(['Ingresos totales', ingresosActual.toFixed(2)]);
    rows.push(['Margen bruto $', (d.ventas_pos?.margen_bruto || 0).toFixed(2)]);
    rows.push(['Margen %', `${d.ventas_pos?.margen_pct ?? 0}%`]);
    rows.push(['Costo vendido', (d.ventas_pos?.costo_vendido || 0).toFixed(2)]);
    rows.push(['Ticket promedio', ((d.ventas_pos?.total || 0) + (d.pedidos_online?.total || 0) > 0 ? (ingresosActual / ((d.ventas_pos?.total || 0) + (d.pedidos_online?.total || 0))).toFixed(2) : '0')]);
    rows.push([]);
    rows.push(['Top productos', '', '', '', '']);
    rows.push(['Producto', 'Código', 'Vendido', 'Ingresos', 'Margen', 'Margen %']);
    (d.top_productos || []).forEach((p) => {
      rows.push([p.nombre, p.codigo, p.vendido, p.ingresos.toFixed(2), p.margen.toFixed(2), p.ingresos > 0 ? `${((p.margen / p.ingresos) * 100).toFixed(0)}%` : '—']);
    });
    rows.push([]);
    rows.push(['Ingresos por día', '', '']);
    rows.push(['Día', 'Ventas', 'Ingresos']);
    (d.ventas_diarias || []).forEach((v) => rows.push([v.dia, v.ventas, v.ingresos.toFixed(2)]));
    const csv = rows.map((r) => r.map((c) => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_${fechaDesde || 'todo'}_a_${fechaHasta || 'todo'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportarPDF = () => {
    if (!data) return;
    const d = data;
    const ingresosActual = (d.ventas_pos?.ingresos || 0) + (d.pedidos_online?.ingresos || 0);
    const topRows = (d.top_productos || []).slice(0, 10)
      .map((p) => `<tr><td>${p.nombre}</td><td>${p.vendido}</td><td>${money(p.ingresos)}</td><td>${money(p.margen)}</td><td>${p.ingresos > 0 ? ((p.margen / p.ingresos) * 100).toFixed(0) : 0}%</td></tr>`)
      .join('');
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Dashboard</title>
      <style>
        body { font-family: Arial, sans-serif; color: #111827; padding: 24px; }
        h1 { font-size: 20px; margin: 0 0 4px; }
        .sub { color: #6b7280; font-size: 12px; margin-bottom: 20px; }
        .kpis { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; }
        .kpi { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 14px; min-width: 120px; }
        .kpi .l { font-size: 10px; color: #6b7280; text-transform: uppercase; font-weight: 700; }
        .kpi .v { font-size: 20px; font-weight: 800; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px; }
        th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; }
        th { background: #f9fafb; color: #6b7280; font-size: 10px; text-transform: uppercase; }
      </style></head><body>
      <h1>Dashboard Agorax</h1>
      <div class="sub">Período: ${fechaDesde || '—'} a ${fechaHasta || '—'}${departamento ? ' · Categoría: ' + departamento : ''}</div>
      <div class="kpis">
        <div class="kpi"><div class="l">Ingresos</div><div class="v">${money(ingresosActual)}</div></div>
        <div class="kpi"><div class="l">Margen bruto</div><div class="v">${money(d.ventas_pos?.margen_bruto || 0)} (${d.ventas_pos?.margen_pct ?? 0}%)</div></div>
        <div class="kpi"><div class="l">Costo vendido</div><div class="v">${money(d.ventas_pos?.costo_vendido || 0)}</div></div>
        <div class="kpi"><div class="l">Pedidos tienda</div><div class="v">${intFmt(d.ventas_pos?.total || 0)}</div></div>
        <div class="kpi"><div class="l">Pedidos online</div><div class="v">${intFmt(d.pedidos_online?.total || 0)}</div></div>
      </div>
      <h2>Top productos</h2>
      <table><thead><tr><th>Producto</th><th>Vendido</th><th>Ingresos</th><th>Margen</th><th>Margen %</th></tr></thead>
      <tbody>${topRows || '<tr><td colspan="5">Sin ventas</td></tr>'}</tbody></table>
      <script>window.onload = function(){ window.print(); }</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  const esExterno = isPlanExterno();

  const filtros = (
    <div
      className="dash-filters"
      style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}
    >
      {PRESETS.map((p) => (
        <button
          key={p.id}
          onClick={() => aplicarPreset(p.id)}
          style={{ ...S.presetBtn, ...(preset === p.id ? S.presetActive : {}) }}
        >
          {p.label}
        </button>
      ))}
      {preset === 'custom' && (
        <>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            style={S.input}
          />
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            style={S.input}
          />
        </>
      )}
      <select value={sede} onChange={(e) => setSede(e.target.value)} style={S.select}>
        <option value="">Todas las sedes</option>
        {sedes.map((s) => (
          <option key={s.codigo} value={s.codigo}>
            {s.nombre}
          </option>
        ))}
      </select>
      <select
        value={departamento}
        onChange={(e) => setDepartamento(e.target.value)}
        style={S.select}
      >
        <option value="">Todas las categorías</option>
        {deptos.map((d) => (
          <option key={d.codigo} value={d.codigo}>
            {d.nombre}
          </option>
        ))}
      </select>
    </div>
  );

  const header = (
    <div
      className="dash-header"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '10px',
      }}
    >
      <div
        style={{
          fontSize: '19px',
          fontWeight: 800,
          color: COL.pri,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <FiBarChart2 size={20} /> Dashboard
      </div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={exportarCSV} style={S.presetBtn}>
          Exportar CSV
        </button>
        <button onClick={exportarPDF} style={S.presetBtn}>
          Exportar PDF
        </button>
        {filtros}
      </div>
    </div>
  );

  const styleBlock = (
    <style>{`
      @keyframes dashShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
      .dash-skel { background: linear-gradient(90deg,#eef0f4 25%,#334155 37%,#eef0f4 63%); background-size: 800px 100%; animation: dashShimmer 1.3s infinite linear; }
      @media (max-width: 768px) {
        .dash-header { flex-direction: column !important; align-items: flex-start !important; }
        .dash-filters { width: 100% !important; }
        .dash-grid-2-1, .dash-grid-1-1 { grid-template-columns: 1fr !important; }
      }
    `}</style>
  );

  // Empresa con sincronizacion ERP (p.ej. Full Chery): dashboard de ventas externas.
  if (syncERP === true) {
    return <DashboardERP />;
  }

  if (cargando && !data) {
    return (
      <div style={S.page}>
        {styleBlock}
        {header}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonBlock key={i} h={92} />
          ))}
        </div>
        <div
          className="dash-grid-2-1"
          style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}
        >
          <SkeletonBlock h={180} />
          <SkeletonBlock h={180} />
        </div>
      </div>
    );
  }

  if (!data)
    return (
      <div style={S.page}>
        {header}
        <Empty>Error cargando datos</Empty>
      </div>
    );

  const d = data;
  const cmp = d.comparativa || null;
  const ingresosActual = (d.ventas_pos?.ingresos || 0) + (d.pedidos_online?.ingresos || 0);
  const sparkRev = [...(d.ventas_diarias || [])].reverse().map((v) => v.ingresos);

  const totalTransacciones = d.ventas_pos.total + d.pedidos_online.total;
  const kpis = [
    {
      l: 'Ingresos totales',
      v: money(ingresosActual),
      s: cmp ? 'vs período anterior' : `${intFmt(d.ventas_pos.total)} ventas`,
      c: COL.green,
      i: FiDollarSign,
      delta: cmp ? { actual: ingresosActual, anterior: cmp.ingresos_total } : null,
      spark: sparkRev,
      big: true,
    },
    {
      l: 'Pedidos',
      v: intFmt(totalTransacciones),
      s: esExterno
        ? money(d.pedidos_online.ingresos)
        : `${intFmt(d.ventas_pos.total)} tienda · ${intFmt(d.pedidos_online.total)} online`,
      c: COL.pri2,
      i: FiShoppingCart,
      delta: cmp
        ? {
            actual: totalTransacciones,
            anterior: (cmp.ventas_pos_count || 0) + (cmp.pedidos_online_count || 0),
          }
        : null,
    },
    {
      l: 'Ticket promedio',
      v: money(totalTransacciones > 0 ? ingresosActual / totalTransacciones : 0),
      s: `${intFmt(totalTransacciones)} transacciones`,
      c: COL.pri2,
      i: FiCreditCard,
      delta:
        cmp && cmp.ventas_pos_count + cmp.pedidos_online_count > 0
          ? {
              actual: ingresosActual / (totalTransacciones || 1),
              anterior: cmp.ingresos_total / (cmp.ventas_pos_count + cmp.pedidos_online_count),
            }
          : null,
    },
    {
      l: 'Margen bruto',
      v: money(d.ventas_pos.margen_bruto),
      s: `${pctFmt(d.ventas_pos.margen_pct)} · costo ${money(d.ventas_pos.costo_vendido)}`,
      c: d.ventas_pos.margen_bruto >= 0 ? COL.green : COL.red,
      i: FiTrendingUp,
      delta:
        cmp && cmp.margen_bruto != null
          ? { actual: d.ventas_pos.margen_bruto, anterior: cmp.margen_bruto }
          : null,
    },
  ];

  const posByDay = {};
  (d.ventas_diarias || []).forEach((v) => {
    posByDay[v.dia] = v.ingresos;
  });
  const pedByDay = {};
  (d.pedidos_diarios || []).forEach((p) => {
    pedByDay[p.dia] = p.ingresos;
  });
  const diasSet = Array.from(new Set([...Object.keys(posByDay), ...Object.keys(pedByDay)]))
    .sort()
    .slice(-30);
  const posSerie = diasSet.map((dd) => posByDay[dd] || 0);
  const pedSerie = diasSet.map((dd) => pedByDay[dd] || 0);
  const totSerie = diasSet.map((dd, i) => posSerie[i] + pedSerie[i]);
  const labels = diasSet.map((dd) => dd.slice(5));
  const baseSerie = esExterno ? pedSerie : totSerie;
  const ma7 = baseSerie.map((_, i) => {
    const a = baseSerie.slice(Math.max(0, i - 6), i + 1);
    return a.reduce((s, x) => s + x, 0) / (a.length || 1);
  });
  const seriesIngresos = esExterno
    ? [
        { name: 'Pedidos', color: COL.purple, values: pedSerie },
        { name: 'Media 7d', color: COL.amber, values: ma7, dash: true },
      ]
    : [
        { name: 'Total', color: COL.green, values: totSerie, area: true },
        { name: 'POS', color: COL.pri2, values: posSerie },
        { name: 'Pedidos', color: COL.purple, values: pedSerie },
        { name: 'Media 7d', color: COL.amber, values: ma7, dash: true },
      ];
  const metodos = d.por_metodo_pago || [];
  const maxMetodo = Math.max(...metodos.map((m) => m.monto), 1);
  const totalMetodos = metodos.reduce((a, m) => a + (m.monto || 0), 0) || 1;

  const lossProds = (d.top_productos || []).filter((p) => (p.margen || 0) < 0);
  const totalPerdida = lossProds.reduce((a, p) => a + (p.margen || 0), 0);

  const pe = d.pedidos_por_estado || {};
  const totalPed = Object.values(pe).reduce((a, b) => a + b, 0);
  const convPct = totalPed ? ((pe.entregado || 0) / totalPed) * 100 : 0;
  const cancPct = totalPed ? (((pe.cancelado || 0) + (pe.rechazado || 0)) / totalPed) * 100 : 0;
  const coloresEstado = {
    pendiente: COL.amber,
    en_revision: '#ff9500',
    aprobado: COL.pri2,
    esperando_pago: '#9c27b0',
    pagado: '#0f9d58',
    armado: '#ff6d00',
    armado_cocina: '#ff6d00',
    listo: COL.pri2,
    en_camino: '#00bcd4',
    llegada: '#009688',
    entregado: COL.green,
    cancelado: COL.muted,
    rechazado: COL.red,
  };

  const maxEstado = Math.max(...Object.values(pe), 1);
  const cardPedidos = (
    <Card title="Embudo de pedidos" icon={FiShoppingBag} color={COL.purple} hint="Pedidos por estado. Conversión = entregados / total. Cancelación = (cancelados + rechazados) / total.">
      {totalPed > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <div
            style={{
              flex: 1,
              background: 'var(--agx-green-bg)',
              borderRadius: '8px',
              padding: '8px 10px',
            }}
          >
            <div
              style={{
                fontSize: '10px',
                color: COL.muted,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <FiCheckCircle size={11} color={COL.green} /> Conversión
            </div>
            <div style={{ fontSize: '17px', fontWeight: 800, color: COL.green }}>
              {convPct.toFixed(0)}%
            </div>
          </div>
          <div
            style={{
              flex: 1,
              background: 'var(--agx-red-bg)',
              borderRadius: '8px',
              padding: '8px 10px',
            }}
          >
            <div
              style={{
                fontSize: '10px',
                color: COL.muted,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <FiXCircle size={11} color={COL.red} /> Cancelación
            </div>
            <div style={{ fontSize: '17px', fontWeight: 800, color: COL.red }}>
              {cancPct.toFixed(0)}%
            </div>
          </div>
        </div>
      )}
      {Object.entries(pe).map(([estado, cant]) => (
        <div key={estado} style={{ marginBottom: '7px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>
              {estado.replace(/_/g, ' ')}
            </span>
            <span style={{ fontWeight: 700 }}>{intFmt(cant)}</span>
          </div>
          <div
            style={{ height: '6px', background: COL.bg, borderRadius: '4px', overflow: 'hidden' }}
          >
            <div
              style={{
                height: '100%',
                width: `${(cant / maxEstado) * 100}%`,
                background: coloresEstado[estado] || COL.muted,
                borderRadius: '4px',
              }}
            />
          </div>
        </div>
      ))}
      {totalPed === 0 && <Empty>Sin pedidos</Empty>}
    </Card>
  );

  const cardInventario = (
    <Card title="Inventario" icon={FiArchive} color={COL.pri2} hint="Valor a la venta = stock × precio. Margen potencial = valor − costo.">
      {(d.inventario.costo_total < 0 || d.inventario.valor_venta < 0) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--agx-red-bg)',
            color: COL.red,
            fontSize: '11px',
            fontWeight: 600,
            borderRadius: '8px',
            padding: '8px 10px',
            marginBottom: '10px',
          }}
        >
          <FiAlertTriangle size={13} /> Stock negativo detectado: corrige el inventario.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        {[
          { l: 'Valor a la venta', v: abbr(d.inventario.valor_venta), c: COL.green },
          { l: 'Costo total', v: abbr(d.inventario.costo_total), c: COL.pri },
          { l: 'Margen potencial', v: abbr(d.inventario.margen_potencial), c: COL.purple },
          { l: 'Productos activos', v: intFmt(d.productos.activos), c: COL.pri2 },
          { l: 'Bajo stock', v: intFmt(d.productos.bajo_stock), c: COL.amber },
          {
            l: 'Agotados',
            v: intFmt(d.productos.agotados),
            c: d.productos.agotados > 0 ? COL.red : COL.green,
          },
        ].map((x, i) => (
          <div key={i} style={{ background: COL.bg, borderRadius: '8px', padding: '10px' }}>
            <div style={{ fontSize: '11px', color: COL.muted, fontWeight: 600 }}>{x.l}</div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: x.c }}>{x.v}</div>
          </div>
        ))}
      </div>
    </Card>
  );

  const cli = d.clientes || { nuevos: 0, recurrentes: 0, total: 0, top: [] };
  const cardClientes = (
    <Card title="Clientes" icon={FiUsers} color={COL.pri2}>
      <Donut
        data={[
          { label: 'Nuevos', value: cli.nuevos || 0, color: COL.pri2 },
          { label: 'Recurrentes', value: cli.recurrentes || 0, color: COL.green },
        ]}
        size={104}
        thickness={16}
      />
      {(cli.top || []).length > 0 && (
        <div
          style={{ marginTop: '12px', borderTop: `1px solid ${COL.border}`, paddingTop: '10px' }}
        >
          <div style={{ fontSize: '11px', fontWeight: 700, color: COL.muted, marginBottom: '6px' }}>
            Top clientes por gasto
          </div>
          {cli.top.slice(0, 5).map((c, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                padding: '3px 0',
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '60%',
                }}
              >
                {c.nombre}
              </span>
              <span style={{ fontWeight: 700, color: COL.green }}>{money(c.gasto)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  const chatD = d.chat || {};
  const iaD = d.ia || { usados: 0, limite: 0 };
  const cardChat = (
    <Card title="Chat y Bot IA" icon={FiMessageCircle} color={COL.purple}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <div style={{ flex: 1, background: COL.bg, borderRadius: '8px', padding: '8px 10px' }}>
          <div style={{ fontSize: '18px', fontWeight: 800, color: COL.pri }}>
            {intFmt(chatD.conversaciones || 0)}
          </div>
          <div style={{ fontSize: '10px', color: COL.muted }}>Conversaciones</div>
        </div>
        <div style={{ flex: 1, background: COL.bg, borderRadius: '8px', padding: '8px 10px' }}>
          <div style={{ fontSize: '18px', fontWeight: 800, color: COL.green }}>
            {intFmt(chatD.con_pedido || 0)}
          </div>
          <div style={{ fontSize: '10px', color: COL.muted }}>Con pedido</div>
        </div>
      </div>
      <div style={{ fontSize: '11px', fontWeight: 700, color: COL.muted, marginBottom: '6px' }}>
        Atención bot vs humano
      </div>
      <Donut
        data={[
          { label: 'Bot', value: chatD.atendidas_bot || 0, color: COL.purple },
          { label: 'Humano', value: chatD.atendidas_humano || 0, color: COL.pri2 },
        ]}
        size={104}
        thickness={16}
      />
      <div style={{ marginTop: '12px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            marginBottom: '3px',
          }}
        >
          <span style={{ fontWeight: 600 }}>Uso de IA</span>
          <span style={{ color: COL.muted }}>
            {intFmt(iaD.usados || 0)} / {iaD.limite ? intFmt(iaD.limite) : '∞'}
          </span>
        </div>
        {iaD.limite > 0 && (
          <div
            style={{ height: '8px', background: COL.bg, borderRadius: '4px', overflow: 'hidden' }}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.min(100, ((iaD.usados || 0) / iaD.limite) * 100)}%`,
                background: (iaD.usados || 0) / iaD.limite > 0.85 ? COL.red : COL.amber,
                borderRadius: '4px',
              }}
            />
          </div>
        )}
      </div>
    </Card>
  );

  const sedesC = d.sedes_comparativa || [];
  const cardSedes = (
    <Card title="Comparación entre sedes" icon={FiMapPin} color={COL.pri2}>
      <BarsH
        items={sedesC.map((s) => ({
          label: s.nombre,
          value: s.ingresos_total,
          sub: `${intFmt(s.ventas)} ventas · ${intFmt(s.pedidos)} pedidos`,
        }))}
        fmt={money}
      />
    </Card>
  );

  const reponer = d.por_reponer || [];
  const cardReponer = (
    <Card title="Productos por reponer" icon={FiAlertTriangle} color={COL.amber}>
      {reponer.length === 0 ? (
        <Empty>Todo en nivel objetivo</Empty>
      ) : (
        reponer.map((p, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '5px 0',
              borderBottom: `1px solid ${COL.border}`,
              fontSize: '12px',
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '58%',
              }}
            >
              {p.nombre}
            </span>
            <span>
              <strong style={{ color: COL.red }}>{intFmt(p.disponible)}</strong>{' '}
              <span style={{ color: COL.muted }}>/ {intFmt(p.objetivo)}</span>
            </span>
          </div>
        ))
      )}
    </Card>
  );

  const deliv = d.delivery || {
    por_repartidor: [],
    puntos: [],
    min_promedio: null,
    a_tiempo_pct: null,
  };
  const cardDelivery = (
    <Card title="Delivery" icon={FiTruck} color="#00897b">
      {(deliv.min_promedio != null || deliv.a_tiempo_pct != null) && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <div style={{ flex: 1, background: COL.bg, borderRadius: '8px', padding: '8px 10px' }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#00897b' }}>
              {deliv.min_promedio != null ? `${deliv.min_promedio} min` : '—'}
            </div>
            <div style={{ fontSize: '10px', color: COL.muted }}>Tiempo promedio</div>
          </div>
          <div style={{ flex: 1, background: COL.bg, borderRadius: '8px', padding: '8px 10px' }}>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 800,
                color:
                  deliv.a_tiempo_pct != null && deliv.a_tiempo_pct >= 80 ? COL.green : COL.amber,
              }}
            >
              {deliv.a_tiempo_pct != null ? `${deliv.a_tiempo_pct}%` : '—'}
            </div>
            <div style={{ fontSize: '10px', color: COL.muted }}>A tiempo</div>
          </div>
        </div>
      )}
      <div style={{ fontSize: '11px', fontWeight: 700, color: COL.muted, marginBottom: '6px' }}>
        Entregas por repartidor
      </div>
      <BarsH
        items={(deliv.por_repartidor || []).map((r) => ({ label: r.nombre, value: r.entregas }))}
        fmt={intFmt}
        color="#00897b"
      />
    </Card>
  );

  const deadStock = d.dead_stock || [];
  const cardDeadStock = (
    <Card title="Stock sin movimiento" icon={FiArchive} color={COL.red}>
      {deadStock.length === 0 ? (
        <Empty>Sin stock estancado</Empty>
      ) : (
        deadStock.map((p, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '5px 0',
              borderBottom: `1px solid ${COL.border}`,
              fontSize: '12px',
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '52%',
              }}
            >
              {p.nombre}
            </span>
            <span style={{ textAlign: 'right' }}>
              <strong style={{ color: COL.red }}>{abbr(p.valor)}</strong>{' '}
              <span style={{ color: COL.muted }}>· {p.dias != null ? `${p.dias}d` : 'nunca'}</span>
            </span>
          </div>
        ))
      )}
    </Card>
  );

  const cardMapa = (deliv.puntos || []).length > 0 && (
    <Card
      title="Mapa de entregas"
      icon={FiMapPin}
      color={COL.purple}
      style={{ marginBottom: '16px' }}
    >
      <MapaEntregas puntos={deliv.puntos} />
    </Card>
  );

  const cardHeatmap = (
    <Card title="Ventas por día y hora" icon={FiBarChart2} color={COL.pri2}>
      <Heatmap data={d.ventas_por_hora || []} />
    </Card>
  );

  const at = d.atencion || {};
  const atItems = [
    {
      l: 'Esperando pago',
      v: at.esperando_pago || 0,
      c: '#9c27b0',
      show: (at.esperando_pago || 0) > 0,
    },
    { l: 'Por verificar', v: at.en_revision || 0, c: '#ff9500', show: (at.en_revision || 0) > 0 },
    { l: 'Agotados', v: at.agotados || 0, c: COL.red, show: (at.agotados || 0) > 0 },
    {
      l: 'Plan vence',
      v: at.plan_dias_restantes != null ? `${at.plan_dias_restantes}d` : '',
      c: COL.red,
      show: at.plan_dias_restantes != null && at.plan_dias_restantes <= 7,
    },
  ].filter((x) => x.show);
  const cardAtencion = atItems.length > 0 && (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
      {atItems.map((x, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: COL.white,
            border: `1px solid ${COL.border}`,
            borderLeft: `4px solid ${x.c}`,
            borderRadius: '10px',
            padding: '8px 14px',
          }}
        >
          <FiAlertTriangle size={15} color={x.c} />
          <div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: x.c, lineHeight: 1 }}>
              {x.v}
            </div>
            <div style={{ fontSize: '10px', color: COL.muted, fontWeight: 600 }}>{x.l}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const cardArmado = (
    <Card
      title={`Armado de hoy ${sede ? '(sede seleccionada)' : ''}`}
      icon={FiUser}
      color="#ff6d00"
      style={{ marginTop: '16px' }}
    >
      {armadores.length === 0 ? (
        <Empty>Sin pedidos armados hoy</Empty>
      ) : (
        <table style={S.table}>
          <thead>
            <tr style={{ background: COL.bg }}>
              {['Armador', 'Pedidos armados', 'Tiempo promedio'].map((h) => (
                <th key={h} style={S.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {armadores.map((a, i) => (
              <tr key={i}>
                <td style={S.td}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}
                  >
                    <FiUser size={12} color="#ff6d00" /> {a.armador_nombre}
                  </div>
                </td>
                <td style={{ ...S.td, fontWeight: 700, color: '#ff6d00' }}>{intFmt(a.armados)}</td>
                <td style={S.td}>
                  {a.minutos_promedio != null ? `${a.minutos_promedio} min` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );

  const cardMetodos = (
    <Card title="Métodos de pago" icon={FiCreditCard} color={COL.green}>
      {metodos.length === 0 ? (
        <Empty />
      ) : (
        metodos.map((m, i) => (
          <div key={i} style={{ marginBottom: '10px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                marginBottom: '3px',
              }}
            >
              <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                {(m.metodo || 'N/A').replace(/_/g, ' ')}
              </span>
              <span style={{ color: COL.green, fontWeight: 700 }}>{money(m.monto)}</span>
            </div>
            <div
              style={{
                height: '7px',
                background: COL.bg,
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(m.monto / maxMetodo) * 100}%`,
                  background: COL.green,
                  borderRadius: '4px',
                }}
              />
            </div>
            <div style={{ fontSize: '10px', color: COL.muted, marginTop: '2px' }}>
              {((m.monto / totalMetodos) * 100).toFixed(0)}% · {intFmt(m.cantidad)} ventas
            </div>
          </div>
        ))
      )}
    </Card>
  );

  const cardTopProductos = (
    <Card title="Top productos" icon={FiPackage} color={COL.pri}>
      {(d.top_productos || []).length === 0 ? (
        <Empty>Sin ventas</Empty>
      ) : (
        <>
          {lossProds.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'var(--agx-red-bg)',
                color: COL.red,
                fontSize: '11px',
                fontWeight: 700,
                borderRadius: '8px',
                padding: '8px 10px',
                marginBottom: '10px',
              }}
            >
              <FiAlertTriangle size={13} />
              {lossProds.length} producto{lossProds.length !== 1 ? 's' : ''} con pérdida (
              {money(totalPerdida)})
            </div>
          )}
          <table style={S.table}>
            <thead>
              <tr style={{ background: COL.bg }}>
                {['Producto', 'Vendido', 'Ingresos', 'Margen', 'Margen %'].map((h) => (
                  <th key={h} style={S.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.top_productos.slice(0, 10).map((p, i) => (
                <tr
                  key={i}
                  style={p.margen < 0 ? { background: 'var(--agx-red-bg)' } : undefined}
                >
                  <td style={S.td}>
                    <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                    <div style={{ color: COL.muted, fontSize: '10px' }}>{p.codigo}</div>
                  </td>
                  <td style={S.td}>{intFmt(p.vendido)}</td>
                  <td style={{ ...S.td, fontWeight: 600, color: COL.green }}>
                    {money(p.ingresos)}
                  </td>
                  <td style={{ ...S.td, color: p.margen > 0 ? COL.green : COL.red }}>
                    {money(p.margen)}
                  </td>
                  <td
                    style={{
                      ...S.td,
                      fontWeight: 700,
                      color: p.margen > 0 ? COL.green : COL.red,
                    }}
                  >
                    {p.ingresos > 0 ? `${((p.margen / p.ingresos) * 100).toFixed(0)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );

  const tabs = [
    { id: 'ventas', label: 'Ventas', show: v('vis.dashboard.graficos') },
    { id: 'inventario', label: 'Inventario', show: v('vis.dashboard.inventario') },
    { id: 'clientes', label: 'Clientes y operación', show: v('vis.dashboard.graficos') },
  ].filter((t) => t.show);
  const activeTab = tabs.some((t) => t.id === tab) ? tab : tabs[0]?.id;

  return (
    <div style={S.page}>
      {styleBlock}
      {header}

      {v('vis.dashboard.financieras') && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          {kpis.map((c, i) => (
            <div
              key={i}
              style={{
                background: COL.white,
                borderRadius: '12px',
                padding: '14px',
                boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
                border: `1px solid ${COL.border}`,
                borderLeft: `4px solid ${c.c}`,
                ...(c.big
                  ? {
                      gridColumn: 'span 2',
                      background: `linear-gradient(135deg, ${COL.green}, #0f9d58)`,
                      border: 'none',
                    }
                  : {}),
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '6px',
                }}
              >
                <span
                  style={{
                    fontSize: '11px',
                    color: c.big ? 'rgba(255,255,255,0.85)' : COL.muted,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                  }}
                >
                  {c.l}
                </span>
                <c.i size={15} color={c.big ? '#fff' : c.c} />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: '6px',
                }}
              >
                <div
                  style={{
                    fontSize: c.big ? '32px' : '22px',
                    fontWeight: 800,
                    color: c.big ? '#fff' : c.c,
                    lineHeight: 1.1,
                  }}
                >
                  {c.v}
                </div>
                {c.spark && c.spark.length > 1 && (
                  <Sparkline data={c.spark} color={c.big ? '#fff' : c.c} />
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <span style={{ fontSize: '11px', color: c.big ? 'rgba(255,255,255,0.85)' : COL.muted }}>
                  {c.s}
                </span>
                {c.delta && <Delta actual={c.delta.actual} anterior={c.delta.anterior} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {cardAtencion}

      <div
        className="dash-tabs"
        style={{
          display: 'flex',
          gap: '6px',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{ ...S.presetBtn, padding: '7px 14px', ...(activeTab === t.id ? S.presetActive : {}) }}
          >
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {onNavigate && v('vis.dashboard.financieras') && (
          <button onClick={() => onNavigate('reportes')} style={S.presetBtn}>
            <FiList size={12} style={{ verticalAlign: '-2px' }} /> Ver reporte completo
          </button>
        )}
      </div>

      {activeTab === 'ventas' && v('vis.dashboard.graficos') && (
        <>
          <div
            className="dash-grid-2-1"
            style={{
              display: 'grid',
              gridTemplateColumns: esExterno ? '1fr' : '2fr 1fr',
              gap: '16px',
              marginBottom: '16px',
            }}
          >
            <Card
              title="Ingresos en el tiempo"
              icon={FiTrendingUp}
              color={COL.green}
              style={{ minWidth: 0, overflow: 'hidden' }}
            >
              <LineChart
                series={seriesIngresos}
                labels={labels}
                fmt={abbr}
                fmtTip={money}
                height={210}
              />
            </Card>
            {!esExterno && cardHeatmap}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px',
              alignItems: 'start',
              marginBottom: '16px',
            }}
          >
            {cardPedidos}
            {!esExterno && cardMetodos}
          </div>
        </>
      )}

      {activeTab === 'inventario' && v('vis.dashboard.inventario') && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px',
            alignItems: 'start',
            marginBottom: '16px',
          }}
        >
          {cardInventario}
          {cardReponer}
          {deadStock.length > 0 && cardDeadStock}
          {!esExterno && cardTopProductos}
        </div>
      )}

      {activeTab === 'clientes' && v('vis.dashboard.graficos') && (
        <>
          {cardMapa}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px',
              alignItems: 'start',
              marginBottom: '16px',
            }}
          >
            {cardClientes}
            {cardChat}
            {sedesC.length > 0 && cardSedes}
            {((deliv.por_repartidor || []).length > 0 || deliv.min_promedio != null) &&
              cardDelivery}
          </div>
          {cardArmado}
        </>
      )}
    </div>
  );
}

// ── Presets de período ──
const PRESETS = [
  { id: '30d', label: '30 días' },
  { id: '7d', label: '7 días' },
  { id: 'hoy', label: 'Hoy' },
  { id: 'mes', label: 'Este mes' },
  { id: 'mesant', label: 'Mes anterior' },
  { id: 'todo', label: 'Todo' },
  { id: 'custom', label: 'Personalizado' },
];

function fechaISO(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const da = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

function rangoDePreset(id) {
  const hoy = new Date();
  if (id === 'hoy') return [fechaISO(hoy), fechaISO(hoy)];
  if (id === '7d') {
    const a = new Date(hoy);
    a.setDate(a.getDate() - 6);
    return [fechaISO(a), fechaISO(hoy)];
  }
  if (id === '30d') {
    const a = new Date(hoy);
    a.setDate(a.getDate() - 29);
    return [fechaISO(a), fechaISO(hoy)];
  }
  if (id === 'mes') {
    const a = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    return [fechaISO(a), fechaISO(hoy)];
  }
  if (id === 'mesant') {
    const a = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    const b = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
    return [fechaISO(a), fechaISO(b)];
  }
  return ['', ''];
}

const S = {
  page: { padding: '20px', overflowY: 'auto', background: COL.bg, minHeight: '100%' },
  select: {
    padding: '6px 10px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    fontSize: '12px',
    background: COL.white,
    fontFamily: 'inherit',
  },
  input: {
    padding: '6px 10px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    fontSize: '12px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    width: '130px',
  },
  presetBtn: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    fontSize: '12px',
    fontWeight: 600,
    background: COL.white,
    color: COL.muted,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  presetActive: { background: COL.pri2, color: COL.white, borderColor: COL.pri2 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: {
    padding: '6px 8px',
    textAlign: 'left',
    fontSize: '10px',
    fontWeight: 700,
    color: COL.muted,
    textTransform: 'uppercase',
  },
  td: { padding: '7px 8px', borderBottom: `1px solid ${COL.border}`, fontSize: '12px' },
};

export default DashboardAdmin;
